// INDEX_SPLIT_B3_v1 (07.09.2026) -- KI-Empfang + Nachrichten: AICallsPage, MessagesPage, RetellSetupCard
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { AlertCircle, Bell, Bot, Calendar, Check, CheckCircle, Eye, Globe, MessageSquare, Mic, MicOff, Phone, PhoneCall, PhoneForwarded, Send, Star, Zap, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, isKosmetikSalon } from "../core/config.jsx";
import { Badge, Btn, Card, Input, Select, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";


// ============================================================
// PAGE: KI-Anrufe
// ============================================================
// AI_CALLS_REAL_v1 (Toby 14.10.2026, DEMO_REST) -- vorher komplett Beispiel: feste Anrufliste, Live-Demo-Gespräch,
// Nummern/Agent/Webhook von „Salon Schönheit“, API-Key-Feld (sichtbar für Tarif pro/premium: Wolf, H-Galerie).
// Jetzt: echte Anrufe aus /api/ai-calls (Retell-Webhook/Sync, ohne interne Kosten). Einrichtung macht Shearly (AI_LOCK).
export const AICallsPage = () => {
  const [calls, setCalls] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    api.request("/ai-calls").then(r => {
      if (r && r.ok && Array.isArray(r.data)) setCalls(r.data);
      else { setCalls([]); setErr((r && r.data && r.data.error) || "Anrufe konnten nicht geladen werden."); }
    }).catch(() => { setCalls([]); setErr("Netzwerk-Fehler"); });
  }, []);
  const resultLabel = { booked: "Termin gebucht", escalated: "Weitergeleitet", cancelled: "Termin abgesagt", rescheduled: "Termin verschoben", info: "Auskunft", voicemail: "Mailbox", missed: "Nicht angenommen" };
  const resultColor = { booked: COLORS.success, escalated: COLORS.warning, cancelled: COLORS.danger, rescheduled: COLORS.info };
  const fmt = v => { if (!v) return "—"; const d = new Date(String(v).replace(" ", "T") + (/[zZ+]/.test(String(v).slice(10)) ? "" : "Z")); return isNaN(d) ? String(v) : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  const dur = s => { const n = Number(s) || 0; return n ? `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")} Min.` : "—"; };
  const list = calls || [];
  const since = Date.now() - 30 * 864e5;
  const last30 = list.filter(c => { const d = new Date(String(c.created_at || "").replace(" ", "T") + "Z"); return !isNaN(d) && d.getTime() >= since; });
  const booked30 = last30.filter(c => c.result === "booked").length;
  const avgSec = last30.length ? Math.round(last30.reduce((n, c) => n + (Number(c.duration_seconds) || 0), 0) / last30.length) : 0;
  const isMobile = window.innerWidth < 768;
  return (
    <div data-feature="AI_CALLS_REAL_v1">
      <Header title="KI-Anrufe" subtitle="Anrufe, die das KI-Telefon für deinen Salon angenommen hat" />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: isMobile ? 8 : 16, marginBottom: isMobile ? 12 : 20 }}>
        <StatCard icon={PhoneCall} label="Anrufe (30 Tage)" value={calls ? String(last30.length) : "…"} color={COLORS.primary} />
        <StatCard icon={Calendar} label="Davon Termin gebucht" value={calls ? String(booked30) : "…"} color={COLORS.success} />
        <StatCard icon={Phone} label="Ø Gesprächsdauer" value={calls ? (avgSec ? dur(avgSec) : "—") : "…"} color={COLORS.info} />
      </div>
      <Card>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Letzte Anrufe</h3>
        <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.5 }}>Rufnummer, Name und Gesprächsprotokoll werden nach 90 Tagen automatisch entfernt. Einrichtung und Änderungen am KI-Telefon übernimmt Shearly — schreib uns einfach.</p>
        {calls === null ? (
          <div style={{ padding: 24, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Lade…</div>
        ) : err ? (
          <div style={{ padding: 24, textAlign: "center", color: COLORS.danger, fontSize: 13 }}>{err}</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Noch keine Anrufe über das KI-Telefon.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  {["Zeit", "Anrufer", "Dauer", "Ergebnis", ""].map((h, i) => (
                    <th key={i} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <React.Fragment key={c.id}>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px", whiteSpace: "nowrap" }}>{fmt(c.created_at)}</td>
                      <td style={{ padding: "10px" }}>{c.caller_name || c.caller_phone ? <><strong>{c.caller_name || ""}</strong>{c.caller_name && c.caller_phone ? " · " : ""}{c.caller_phone || ""}</> : <span style={{ color: COLORS.textLight }}>entfernt (90 Tage)</span>}</td>
                      <td style={{ padding: "10px", whiteSpace: "nowrap" }}>{dur(c.duration_seconds)}</td>
                      <td style={{ padding: "10px" }}>{c.result ? <Badge text={resultLabel[c.result] || c.result} color={resultColor[c.result] || COLORS.textLight} /> : "—"}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{c.transcript ? <button onClick={() => setOpenId(openId === c.id ? null : c.id)} style={{ background: "none", border: "none", color: COLORS.primary, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>{openId === c.id ? "Protokoll zu" : "Protokoll"}</button> : null}</td>
                    </tr>
                    {openId === c.id && c.transcript ? (
                      <tr><td colSpan="5" style={{ padding: "8px 10px 14px", fontSize: 12, color: COLORS.text, whiteSpace: "pre-wrap", lineHeight: 1.5, background: "var(--cardHover)" }}>{c.transcript}</td></tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ============================================================
// PAGE: Messages / SMS
// ============================================================
export const MessagesPage = ({ customers, onGoToSubscription }) => {
  /* SMS_TRIAL_LOCK_v1 -- Fenster "erst nach Kauf", wenn ein Trial-Salon eine SMS-Automatik einschalten will */
  const [trialLockModal, setTrialLockModal] = useState(false);
  // SMS_LIVE_v1 -- ersetzt Demo-Daten (Salon Schönheit, Sarah, Klaus) durch echte
  // twilio_sms_logs aus /api/salon/sms-stats.
  const [smsData, setSmsData] = useState(null);
  const [smsLoading, setSmsLoading] = useState(true);
  useEffect(() => {
    api.request("/salon/sms-stats").then(r => {
      if (r && r.ok) setSmsData(r.data || {});
      setSmsLoading(false);
    }).catch(() => setSmsLoading(false));
  }, []);

  // SMS-Typ aus Body-Excerpt grob klassifizieren (Backend speichert keinen type)
  const inferType = (preview) => {
    const p = (preview || "").toLowerCase();
    if (/erinnerung|reminder|morgen|heute um|bis gleich/.test(p)) return "reminder";
    if (/geburtstag|happy|🎂|bday/.test(p)) return "birthday";
    if (/buchung|gebucht|bestätigung|reserviert/.test(p)) return "confirm";
    if (/bewertung|google|review|sterne/.test(p)) return "review";
    return "info";
  };
  const typeConfig = {
    reminder: { label: "Erinnerung", color: COLORS.info, icon: Bell },
    birthday: { label: "Geburtstag", color: COLORS.accent, icon: Star },
    confirm:  { label: "Bestätigung", color: COLORS.success, icon: CheckCircle },
    review:   { label: "Bewertung", color: COLORS.warning, icon: Star },
    info:     { label: "SMS", color: COLORS.primary, icon: Send },
  };
  const statusColor = (s) => {
    const x = (s || "").toLowerCase();
    if (x === "delivered" || x === "sent") return COLORS.success;
    if (x === "queued" || x === "sending" || x === "accepted") return COLORS.info;
    if (x === "failed" || x === "undelivered") return COLORS.danger;
    return COLORS.textLight;
  };
  const statusLabel = (s) => {
    const x = (s || "").toLowerCase();
    if (x === "delivered") return "Zugestellt";
    if (x === "sent") return "Gesendet";
    if (x === "queued" || x === "accepted") return "In Warteschlange";
    if (x === "sending") return "Sende...";
    if (x === "failed") return "Fehlgeschlagen";
    if (x === "undelivered") return "Nicht zugestellt";
    return s || "—";
  };
  const fmtAt = (at) => {
    if (!at) return "";
    try {
      const d = new Date(at);
      return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return at; }
  };

  const recent = (smsData && smsData.recent) || [];
  const monthCount = smsData && smsData.month && smsData.month.count;
  const todayCount = smsData && smsData.today && smsData.today.count;
  const last30Count = smsData && smsData.last30 && smsData.last30.count; /* SMS_COST_HIDE_v1 */

  return (
    <div>
      <Header title="SMS & Nachrichten" subtitle="Automatische Erinnerungen & Marketing" />

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard icon={Send} label="SMS gesendet (Monat)" value={smsLoading ? "…" : String(monthCount || 0)} color={COLORS.primary} />
        <StatCard icon={Bell} label="SMS gesendet (heute)" value={smsLoading ? "…" : String(todayCount || 0)} color={COLORS.info} />
        <StatCard icon={MessageSquare} label="SMS letzte 30 Tage" value={smsLoading ? "…" : String(last30Count || 0)} color={COLORS.accent} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "2fr 1fr", gap: 16 }}>
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Letzte Nachrichten</h3>
          {smsLoading ? (
            <div style={{ padding: 24, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Wird geladen…</div>
          ) : recent.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: COLORS.textLight, fontSize: 13, lineHeight: 1.5 }}>
              Noch keine SMS gesendet.<br/>
              <span style={{ fontSize: 12 }}>Sobald eine Buchungsbestätigung, Erinnerung oder Geburtstags-SMS rausgeht, erscheint sie hier.</span>
            </div>
          ) : recent.map((msg, idx) => {
            const t = inferType(msg.preview);
            const cfg = typeConfig[t];
            const Icon = cfg.icon;
            return (
              <div key={idx} style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${cfg.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} color={cfg.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.text }}>+••• {msg.to || "—"}</span>
                    <span style={{ fontSize: 11, color: COLORS.textLight, whiteSpace: "nowrap" }}>{fmtAt(msg.at)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {msg.preview || <i>(kein Inhalt gespeichert)</i>}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Badge text={cfg.label} color={cfg.color} />
                    <Badge text={statusLabel(msg.status)} color={statusColor(msg.status)} />
                  </div>
                </div>
              </div>
            );
          })}
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>SMS-Automatisierung</h3>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px" }}>Wähle aus, welche SMS automatisch an deine Kunden gehen. Änderungen werden sofort gespeichert.</p>
          {/* SMS_QUOTA_v1 -- Verbrauchsbalken, nur wenn das Kontingent fuer den Salon aktiv ist */}
          {smsData && smsData.quota && smsData.quota.enabled && (() => {
            const q = smsData.quota;
            const pct = q.included > 0 ? Math.min(100, Math.round(q.used / q.included * 1000) / 10) : 100;
            const barColor = pct >= 100 ? COLORS.danger : pct >= 80 ? "#F59E0B" : COLORS.success;
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
                  <span>SMS-Kontingent diesen Monat</span>
                  <span>{q.used} / {q.included} ({pct}%)</span>
                </div>
                <div style={{ height: 8, background: "var(--cardHover)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: Math.min(100, pct) + "%", background: barColor, transition: "width 0.3s" }} />
                </div>
                <div style={{ fontSize: 10, color: (q.overage > 0 && q.billing_active) ? COLORS.danger : COLORS.textLight, marginTop: 4 }}>
                  {/* SMS_QUOTA_BAR_NOW_v1 -- Kostenhinweis nur bei aktiver Abrechnung + Ueberschreitung */}
                  {q.overage > 0 && q.billing_active
                    ? `${q.overage} SMS über dem Kontingent — jede weitere ${q.overage_price_eur.toFixed(2)} € netto, automatische Abrechnung am Monatsanfang.`
                    : (q.overage > 0 ? `Kontingent diesen Monat ausgeschöpft.` : `Noch ${q.remaining} Inklusiv-SMS diesen Monat.`)}
                </div>
              </div>
            );
          })()}
          {(() => {
            const [smsSettings, setSmsSettings] = useState(null);
            const [smsMsg, setSmsMsg] = useState("");
            useEffect(() => {
              api.request("/salon/sms-settings").then(res => {
                if (res.ok) setSmsSettings(res.data);
                else setSmsSettings({ sms_booking_confirm: true, sms_reminder_24h: true, sms_reminder_2h: true, sms_manual_booking: false, sms_review_request: true });
              });
            }, []);
            const toggleSms = async (field) => {
              const turningOn = !smsSettings[field];
              /* SMS_TRIAL_LOCK_v1 -- im Trial: Einschalten blockiert, Fenster mit Hinweis auf den Kauf */
              if (turningOn && smsSettings.trial_locked && field !== "prefer_email_notifications" && field !== "booking_email_required") { setTrialLockModal(true); return; }
              const updated = { ...smsSettings, [field]: turningOn };
              setSmsSettings(updated);
              setSmsMsg("");
              const res = await api.request("/salon/sms-settings", { method: "PUT", body: JSON.stringify({ [field]: updated[field] }) });
              if (res.ok) setSmsMsg("Gespeichert");
              else if (res.status === 403 && res.data && res.data.code === "TRIAL_SMS_LOCKED") { setSmsSettings({ ...smsSettings, trial_locked: true }); setTrialLockModal(true); }
              else setSmsMsg("Fehler beim Speichern");
              setTimeout(() => setSmsMsg(""), 2000);
            };
            const smsTypes = [
              // SMS_OWNER_TRIM_v1 -- Inhaber sieht nur noch Bestätigung + 24h-Erinnerung.
              // Manuelle Buchung, 2h-Erinnerung, Google-Bewertung steuert nur der Admin (Toby) pro Salon.
              { key: "sms_booking_confirm", label: "Buchungsbestätigung", desc: "SMS direkt nach einer Online-Buchung" },
              { key: "sms_reminder_24h", label: "24h-Erinnerung", desc: "Erinnerung am Vortag des Termins" },
              // EMAIL_FIRST_v1 -- Kanal-Wahl: E-Mail zuerst, SMS nur ohne E-Mail-Adresse
              { key: "prefer_email_notifications", label: "Bevorzugt per E-Mail senden", desc: "Bestätigung und Erinnerung gehen per E-Mail, wenn beim Kunden eine E-Mail-Adresse hinterlegt ist. SMS nur noch für Kunden ohne E-Mail-Adresse — das spart SMS-Kosten." },
              // BOOKING_EMAIL_REQUIRED_v1 (Toby 30.09.2026) -- E-Mail Pflichtfeld auf der Buchungsseite
              { key: "booking_email_required", label: "E-Mail bei der Online-Buchung ist Pflicht", desc: "Kunden müssen beim Online-Buchen zusätzlich zu Name und Telefon ihre E-Mail-Adresse angeben. Sie wird in der Kundenkarte gespeichert, wenn dort noch keine steht. Ohne E-Mail ist online kein Termin möglich." },
            ];
            if (!smsSettings) return <div style={{ fontSize: 13, color: "#6b7280" }}>Lädt…</div>;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {smsTypes.map(t => (
                  <div key={t.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: smsSettings[t.key] ? "rgba(34,197,94,0.10)" : "var(--cardHover)", borderRadius: 8, border: "1px solid " + (smsSettings[t.key] ? "rgba(34,197,94,0.35)" : "var(--border)"), cursor: "pointer" }} onClick={() => toggleSms(t.key)}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "var(--textLight)" }}>{t.desc}</div>
                    </div>
                    <div style={{ width: 40, height: 22, borderRadius: 11, background: smsSettings[t.key] ? "#22c55e" : "#6B7280", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: smsSettings[t.key] ? 20 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                    </div>
                  </div>
                ))}
                {smsMsg && <div style={{ marginTop: 10, fontSize: 12, color: smsMsg === "Gespeichert" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{smsMsg}</div>}
                {/* SMS_TRIAL_LOCK_v1 -- Hinweis unter den Schaltern, solange der Salon im Trial ist */}
                {smsSettings.trial_locked && (
                  <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textLight, lineHeight: 1.5, padding: "8px 10px", background: "var(--cardHover)", borderRadius: 8 }}>
                    🔒 SMS-Versand ist in der Testphase noch nicht verfügbar. {onGoToSubscription ? <a href="#" onClick={e => { e.preventDefault(); onGoToSubscription(); }} style={{ color: COLORS.primary, fontWeight: 600 }}>Tarif wählen</a> : "Nach dem Kauf eines Tarifs schaltbar."}
                  </div>
                )}
              </div>
            );
          })()}
        </Card>
      </div>
      {/* SMS_TRIAL_LOCK_v1 -- Fenster beim Versuch, im Trial eine SMS-Automatik einzuschalten */}
      {trialLockModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setTrialLockModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: COLORS.card, borderRadius: 14, padding: 22, width: "100%", maxWidth: 440, boxShadow: "0 12px 40px rgba(0,0,0,0.32)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>🔒 SMS-Versand ab dem gebuchten Tarif</div>
            <p style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6, margin: "0 0 14px" }}>
              In der Testphase bleibt der SMS-Versand ausgeschaltet. Sobald du einen Tarif gebucht hast, kannst du Buchungsbestätigung und 24h-Erinnerung per SMS einschalten – <b>400 SMS pro Monat sind inklusive</b>.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {onGoToSubscription && (
                <button onClick={() => { setTrialLockModal(false); onGoToSubscription(); }}
                  style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Tarif wählen</button>
              )}
              <button onClick={() => setTrialLockModal(false)}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Später</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin: Salon-Verwaltung
// RETELL_PROVISION_v1 — Self-Service-Setup-Card im Admin-Salon-Modal
// Zeigt aktuellen Stand (Agent vorhanden? Phone-Nummer? Aktiviert?) + Provision-Button.
export const RetellSetupCard = ({ salonId, salonName }) => {
  const [status, setStatus] = React.useState(null);
  const [provisioning, setProvisioning] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [msgType, setMsgType] = React.useState(""); // 'ok' | 'err'

  const load = () => {
    setMsg("");
    fetch(`/api/admin/salons/${salonId}/retell-status`, {
      headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
    }).then(r => r.json()).then(d => setStatus(d)).catch(() => setStatus({ error: "Status nicht ladbar" }));
  };
  React.useEffect(() => { if (salonId) load(); }, [salonId]);

  const provision = async () => {
    if (!confirm(`KI-Telefonie-Agent für "${salonName || 'diesen Salon'}" provisionieren?\n\n` +
                 `Was passiert:\n` +
                 `• Neuer Retell-Agent mit salon-spezifischem Prompt wird erstellt\n` +
                 `• Begrüßungs-Text mit Salonnamen + aktuellem Datum\n` +
                 `• Services + Mitarbeiter werden eingelesen\n\n` +
                 `Kosten: 0 € (Phone-Nummer wird NICHT gekauft).\n` +
                 `Danach: Nummer kaufen + im Dashboard eintragen.`)) return;
    setProvisioning(true);
    setMsg("");
    try {
      const r = await fetch(`/api/admin/salons/${salonId}/provision-retell`, {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + sessionStorage.getItem("sp_token"),
          "Content-Type": "application/json",
        },
        body: "{}",
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setMsgType("ok");
        setMsg(d.message || "Agent provisioniert");
        load();
      } else {
        setMsgType("err");
        setMsg((d.detail || d.error || "Fehler") + (r.status === 502 ? " (Retell-API)" : ""));
      }
    } catch (e) {
      setMsgType("err");
      setMsg("Netzwerk-Fehler: " + (e.message || "?"));
    }
    setProvisioning(false);
  };

  if (!status) return (
    <div style={{ marginLeft: 26, fontSize: 12, color: COLORS.textLight, padding: "6px 0" }}>
      Lade KI-Telefonie-Status…
    </div>
  );
  if (status.error) return null; // still verstecken bei API-Fehler — Toby sieht's im normalen Modal-Save

  const hasAgent = !!status.has_agent;
  const hasPhone = !!status.has_phone;
  const live = hasAgent && hasPhone && status.enabled;

  const StatusBadge = ({ ok, warn, label }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: ok ? "rgba(16,185,129,0.12)" : warn ? "rgba(245,158,11,0.12)" : "rgba(156,163,175,0.12)",
      color: ok ? "#059669" : warn ? "#B45309" : "#6B7280",
    }}>
      {ok ? "✓" : warn ? "○" : "✗"} {label}
    </span>
  );

  return (
    <div style={{
      marginLeft: 26, marginTop: 4,
      background: "var(--cardHover)", border: "1px solid var(--border)",
      borderRadius: 8, padding: "10px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>
          🤖 Retell-Agent-Setup
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <StatusBadge ok={hasAgent} label={hasAgent ? "Agent bereit" : "Kein Agent"} />
          <StatusBadge ok={hasPhone} warn={hasAgent && !hasPhone} label={hasPhone ? "Nummer da" : "Nummer fehlt"} />
          <StatusBadge ok={live} warn={hasAgent && !live} label={live ? "Live" : "Nicht live"} />
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.55, marginBottom: 8 }}>
        {!hasAgent && (
          <>Provisioniere einen Retell-Agent mit salon-spezifischem Prompt
            ({status.service_count || 0} Services). Kostet 0 €. Erst beim Phone-Number-Kauf entstehen Kosten.</>
        )}
        {hasAgent && !hasPhone && (
          <>Agent <code style={{ fontSize: 10 }}>{(status.agent_id || "").slice(0, 16)}…</code> ist bereit.
            <b> Nächster Schritt:</b> Telefonnummer in der Retell-Console kaufen + im Salon-Profil eintragen.
            Re-Provisionieren überschreibt den Agent (Prompt wird neu aus aktuellen Daten gebaut).</>
        )}
        {hasAgent && hasPhone && (
          <>Live unter <code style={{ fontSize: 11 }}>{status.phone}</code>. Voice: {status.voice_id || "-"}, Locale: {status.locale || "-"}.
            Re-Provisionieren regeneriert den Prompt aus aktuellen Salon-Daten.</>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={provision}
          disabled={provisioning}
          style={{
            padding: "7px 12px", borderRadius: 6, border: "1px solid " + COLORS.primary,
            background: COLORS.primary, color: "#fff",
            fontSize: 12, fontWeight: 700, cursor: provisioning ? "wait" : "pointer",
            opacity: provisioning ? 0.6 : 1,
          }}>
          {provisioning ? "Provisioniere…" : (hasAgent ? "🔄 Re-Provisionieren" : "📞 Agent provisionieren")}
        </button>
        {status.activated_at && (
          <span style={{ fontSize: 10, color: COLORS.textLight, alignSelf: "center" }}>
            Zuletzt aktiviert: {new Date(status.activated_at).toLocaleString("de-DE")}
          </span>
        )}
      </div>

      {msg && (
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 6, fontSize: 11.5,
          background: msgType === "ok" ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
          color: msgType === "ok" ? "#059669" : "#991B1B",
        }}>
          {msgType === "ok" ? "✓ " : "✗ "}{msg}
        </div>
      )}
    </div>
  );
};